import { Component, ViewChild } from "@angular/core";
import { IAddressValidationModel } from "@models";
import { SlickConfirmDialogComponent, SlickConfirmDialogResults } from "@slick-components";
import { GlobalsService, TaxRatesService } from "@services";

/** The shape any caller needs to supply -- shipping address, customer shipping fields, etc. */
export interface IValidatableAddress {
	address1: string;
	address2?: string;
	city: string;
	state: string;
	zipcode: string;
}

export interface IAddressValidationDialogResult {
	/** False only when the user chose to go back and fix an address CereTax rejected. */
	proceed: boolean;
	/** Null when there was no verdict -- company isn't on CereTax, address too sparse, or CereTax was unreachable. */
	validation: IAddressValidationModel;
	/** True when the caller should copy the standardized address over what the user typed. */
	useSuggested: boolean;
}

@Component({
	selector: "address-validation-dialog",
	template: `
<slick-confirm-dialog [width]="440" okButtonText="Use Recommended" cancelButtonText="Keep As Entered" #suggestedAddressConfirmRef>
    <div class="mb-2">
        <div class="text-muted">As entered</div>
        <div>{{enteredAddressText}}</div>
    </div>
    <div>
        <div class="text-muted">Recommended</div>
        <div class="fw-bold">{{suggestedAddressText}}</div>
    </div>
</slick-confirm-dialog>

<slick-confirm-dialog [width]="440" okButtonText="Save Anyway" cancelButtonText="Go Back" #invalidAddressConfirmRef>
    <div class="text-danger mb-2">We could not validate this address.</div>
    <div class="mb-2">{{validationErrorMessage}}</div>
    <div class="text-muted">Saving anyway may result in tax being calculated from the zipcode only.</div>
</slick-confirm-dialog>`
})

export class AddressValidationDialogComponent {
	@ViewChild("suggestedAddressConfirmRef") suggestedAddressConfirmRef: SlickConfirmDialogComponent;
	@ViewChild("invalidAddressConfirmRef") invalidAddressConfirmRef: SlickConfirmDialogComponent;

	enteredAddressText: string;
	suggestedAddressText: string;
	validationErrorMessage: string;

	constructor(private taxRatesService: TaxRatesService) { }

	// Anything unexpected fails open -- a CereTax outage shouldn't stop someone from saving.
	async validate(address: IValidatableAddress): Promise<IAddressValidationDialogResult> {
		const noVerdict: IAddressValidationDialogResult = { proceed: true, validation: null, useSuggested: false };

		if (!GlobalsService.company.useCereTax || !address)
			return noVerdict;

		let validation: IAddressValidationModel;

		try {
			validation = await this.taxRatesService.validateAddress(<any>address);
		}
		catch {
			return noVerdict;
		}

		if (!validation)
			return noVerdict;

		if (!validation.isValid) {
			this.validationErrorMessage = validation.errorMessage;

			const result = await this.invalidAddressConfirmRef.confirm();

			return { proceed: (result === SlickConfirmDialogResults.Ok), validation: validation, useSuggested: false };
		}

		if (validation.addressChanged) {
			this.enteredAddressText = this.formatAddress(address);
			this.suggestedAddressText = validation.fullAddress;

			const result = await this.suggestedAddressConfirmRef.confirm();

			return { proceed: true, validation: validation, useSuggested: (result === SlickConfirmDialogResults.Ok) };
		}

		// Nothing for the user to decide, so take the geocode and plus4 silently
		return { proceed: true, validation: validation, useSuggested: true };
	}

	private formatAddress(address: IValidatableAddress): string {
		const cityStateZip = [address.city, address.state, address.zipcode]
			.filter(x => !!x)
			.join(" ");

		return [address.address1, address.address2, cityStateZip]
			.filter(x => !!x)
			.join(", ");
	}
}
